'use client';

import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Image as ImageIcon, Loader2, Check, X, Edit, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PopupTemplate from '@/app/ui/popupTemplate';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useGlobalChatState } from '../../hooks/useGlobalChatState';
import { LiveImageStatus } from '../../data/liveMessageData/liveMessage';
import { useImageGenAPI } from '../../hooks/useImageGenApi';
import { getCurrentImageModelPrice } from '../../data/chatSetting/settingOptions';
import { useLiveMessageActions, useLiveMessages } from '../../hooks/useLiveMessages';
import { useCharacterData } from '../../hooks/useGlobalChatState';
import { useAuth } from '@/app/user-and-session/session-auth/hooks/useAuth';
import { useUserAppData } from '@/app/user-and-session/user-app-data/useUserAppData';
import { useLocale } from '@/app/user-and-session/languages/i18n/locale-provider';
import { generatePresignedUrl } from '@/app/lib/actions/s3';
import { uploadImageToCloudinary } from '@/app/lib/cloudinary/cloudinary';
import ImageSelectionPopup from './ImageSelectionPopup';
import Base64ImagePreview from './Base64ImagePreview';
import InsufficientCreditPopup from '@/app/popup/InsufficientCreditPopup';
import type { Locale } from '@/app/api/chat/systemp_prompt/system_prompt';

async function getImageAsBase64(imageUrl: string): Promise<string | null> {
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error converting Cloudinary image to base64:', error);
    return null;
  }
}

interface ImageGenPopupNewProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ImageGenPopupNew({ isOpen, onClose }: ImageGenPopupNewProps) {
  const t = useTranslations('chat');
  const localeContext = useLocale();
  const locale: Locale = (localeContext?.locale === 'en' || localeContext?.locale === 'zh-Hans') 
    ? localeContext.locale 
    : 'en';
  const [globalChatStateData, globalChatStateActions] = useGlobalChatState();
  const [base64Image, setBase64Image] = useState<string | null>(null);
  const [isLoadingBase64, setIsLoadingBase64] = useState(false);
  const [imagePrompt, setImagePrompt] = useState('');

  // Image generation states
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [imageGenStatus, setImageGenStatus] = useState<'idle' | 'generating' | 'success' | 'error' | 'applying'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const [showImageSelectionPopup, setShowImageSelectionPopup] = useState(false);
  const [selectedImage, setSelectedImage] = useState<any>(null);
  const [localReferenceImage, setLocalReferenceImage] = useState<string | null>(null);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [showInsufficientCreditPopup, setShowInsufficientCreditPopup] = useState(false);

  // Get live messages for image selection
  const messages = useLiveMessages();

  // Filter messages for gallery images (same logic as TextChat)
  const galleryImages = useMemo(() => {
    return messages.filter((msg: any) => {
      const isMsgAssistantImage = msg.role === 'assistant' && msg.imageUrl;
      return isMsgAssistantImage;
    });
  }, [messages]);

  // textarea ref for auto-resize
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea function
  const autoResizeTextarea = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, []);

  // Set initial textarea height
  useEffect(() => {
    autoResizeTextarea();
  }, [autoResizeTextarea]);

  // Initialize local reference image from the last message with image when popup opens
  useEffect(() => {
    if (isOpen) {
      // Find the last message with an image
      const lastImageMessage = messages
        .filter(msg => msg.role === 'assistant' && msg.imageUrl)
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
      
      if (lastImageMessage && lastImageMessage.imageUrl) {
        setLocalReferenceImage(lastImageMessage.imageUrl);
        setSelectedImage(lastImageMessage);
      } else {
        setLocalReferenceImage(null);
        setSelectedImage(null);
      }
    }
  }, [isOpen, messages]);

  // Get necessary hooks for message handling
  const messageActions = useLiveMessageActions();
  const { character } = useCharacterData();
  const { userEmail, isAuthenticated, isLoading: authLoading } = useAuth();
  
  // User app data hook for gem count (singleton) - only after auth loading completes
  const [userAppDataState] = useUserAppData(
    !authLoading && isAuthenticated && userEmail ? userEmail : ''
  );
  
  // Use image generation API hook
  const { generateImage, isGenerating, resetState } = useImageGenAPI({
    onImageGenerated: (imageBase64: string) => {
      setGeneratedImage(imageBase64);
      setImageGenStatus('success');
      setErrorMessage(null);
    }
  });

  useEffect(() => {
    const fetchAndLogBase64 = async () => {
      // Find the message that corresponds to the local reference image
      const referenceMessage = messages.find(msg => msg.imageUrl === localReferenceImage);
      
      if (referenceMessage?.imageWithStatus?.imageData && typeof referenceMessage.imageWithStatus.imageData.toURL === 'function') {
        setIsLoadingBase64(true);
        const imageUrl = referenceMessage.imageWithStatus.imageData.toURL();
        const base64Data = await getImageAsBase64(imageUrl);
        setBase64Image(base64Data);
        setIsLoadingBase64(false);
      } else {
        setBase64Image(null);
      }
    };

    fetchAndLogBase64();
  }, [localReferenceImage, messages]);

  // Reset states when popup opens or closes
  useEffect(() => {
    if (isOpen) {
      setImagePrompt('');
      setGeneratedImage(null);
      setImageGenStatus('idle');
      setErrorMessage(null);
      resetState(); // Reset the hook's internal state
    } else {
      // Also reset when popup closes to ensure clean state
      setImagePrompt('');
      setGeneratedImage(null);
      setImageGenStatus('idle');
      setErrorMessage(null);
      setBase64Image(null); // Reset the reference image when closing
      resetState(); // Reset the hook's internal state
    }
  }, [isOpen, resetState]);

  // Handle image generation
  const handleGenerateImage = async () => {
    if (!base64Image || !imagePrompt.trim()) {
      return;
    }

    // Check if user has sufficient credits before generating
    try {
      const currentImageModelPrice = getCurrentImageModelPrice(globalChatStateData.userChatSetting.imageModel);
      
      // Use singleton gem count for faster access
      if (userAppDataState.creditCount + userAppDataState.dailyCreditCount < currentImageModelPrice) {
        setShowInsufficientCreditPopup(true);
        return;
      }
    } catch (error) {
      console.error('Error checking image credits:', error);
      // If credit check fails, still allow generating (fail-safe approach)
    }

    setImageGenStatus('generating');
    setErrorMessage(null);

    try {
      const imageData = {
        filename: 'input_image.png',
        base64: base64Image,
        width: 768, // We could extract actual dimensions if needed
        height: 1344
      };

      const result = await generateImage(imageData, imagePrompt);

      if (!result.success) {
        if (result.error === 'insufficient_credits') {
          setShowInsufficientCreditPopup(true);
        } else {
          setImageGenStatus('error');
          setErrorMessage(result.error || 'Failed to generate image');
        }
      }
    } catch (error) {
      setImageGenStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Unknown error occurred');
    }
  };

  // Handle regenerate
  const handleRegenerate = () => {
    setImageGenStatus('idle');
    setGeneratedImage(null);
    setErrorMessage(null);
  };

  // Handle keep editing - use generated image as new reference
  const handleKeepEditing = () => {
    if (generatedImage) {
      // Set the generated image as the new reference image
      setBase64Image(generatedImage);
      // Reset to idle state for new generation
      setImageGenStatus('idle');
      setGeneratedImage(null);
      setErrorMessage(null);
      // Keep the same prompt for continued editing
    }
  };

  // Handle applying the generated image - upload to S3, Cloudinary, and add to datahub
  const handleApplyImage = async () => {
    if (!generatedImage || !userEmail || !character?.metadata.avatar_id) {
      console.error('Missing required data for applying image');
      return;
    }

    setIsApplying(true);
    setImageGenStatus('applying');

    try {
      // Step 1: Convert base64 to blob for upload
      const response = await fetch(generatedImage);
      const blob = await response.blob();
      
      // Step 2: Generate unique keys for S3 and Cloudinary
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2, 8);
      const s3Key = `rita-swap-images/${userEmail}/${character.metadata.avatar_id}/${timestamp}-${randomId}.jpg`;
      const cloudinaryKey = `rita-swap-images/${userEmail}/${character.metadata.avatar_id}/${timestamp}-${randomId}`;

      // Run three operations concurrently: S3 upload, Cloudinary upload, and image captioning
      const [s3UploadResponse, cloudinaryResult, captionResult] = await Promise.all([
        // S3 upload
        (async () => {
          const presignedUrlResponse = await generatePresignedUrl(s3Key);
          const s3UploadResponse = await fetch(presignedUrlResponse.presignedUrl, {
            method: 'PUT',
            body: blob,
            headers: {
              'Content-Type': 'image/jpeg',
            },
          });
          if (!s3UploadResponse.ok) {
            throw new Error('Failed to upload image to S3');
          }
          return s3UploadResponse;
        })(),
        
        // Cloudinary upload
        (async () => {
          const file = new File([blob], 'generated-image.jpg', { type: 'image/jpeg' });
          const cloudinaryResult = await uploadImageToCloudinary(file, undefined, cloudinaryKey);
          if (!cloudinaryResult || !cloudinaryResult.public_id) {
            throw new Error('Failed to upload image to Cloudinary');
          }
          return cloudinaryResult;
        })(),
        
        // Image captioning
        (async () => {
          // Extract base64 data from the data URL
          const base64Data = generatedImage.split(',')[1];
          const response = await fetch('/api/image_caption', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              imageData: base64Data,
              mimeType: 'image/jpeg',
              locale: locale,
            }),
          });
          if (!response.ok) {
            throw new Error(`Image caption API error: ${response.status}`);
          }
          return await response.json();
        })()
      ]);

      // Create message content with caption and prompt (matching Python backend format)
      let messageContent = imagePrompt;
      if (captionResult.success && captionResult.caption) {
        messageContent = `Image description: ${captionResult.caption}\nImage edit prompt: ${imagePrompt}`;
      }

      // Step 5: Add message to datahub with the S3 key as imageUrl
      const messageId = `img-${timestamp}-${randomId}`;
      await messageActions.addMessage({
        id: messageId,
        content: messageContent,
        role: 'assistant',
        sender_id: character.metadata.avatar_id,
        sender_name: character.metadata.avatar_name || 'Assistant',
        created_at: new Date().toISOString(),
        imageUrl: s3Key, // Use S3 key as imageUrl for datahub
        isStreaming: false,
      });

      console.log('Successfully applied generated image:', {
        s3Key,
        cloudinaryPublicId: cloudinaryResult.public_id,
        messageId
      });      
      
      // step 6: set the active image to the newly generated one
      // This now includes automatic head detection and image swap for live chat
      await globalChatStateActions.setActiveImage(s3Key);

      // Close the popup on success
      onClose();

    } catch (error) {
      console.error('Error applying generated image:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Failed to apply image');
      setImageGenStatus('error');
    } finally {
      setIsApplying(false);
    }
  };

  const handleImageSelect = (message: any) => {
    setSelectedImage(message);
    console.log('Image selected:', message);
  };

  const handleApplySelectedImage = async () => {
    if (!selectedImage) return;
    
    try {
      // Update only the local reference image state
      setLocalReferenceImage(selectedImage.imageUrl);
      console.log('Local reference image updated:', selectedImage.imageUrl);
    } catch (error) {
      console.error('Error applying image:', error);
    }
  };

  return (
    <PopupTemplate
      isOpen={isOpen}
      onClose={onClose}
      closeable={true}
      isTransparent={false}
      className="max-h-[90vh] flex flex-col overflow-hidden"
    >
      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b border-white/10">
        <div className="flex items-center gap-2">
          <ImageIcon className="w-5 h-5 text-white" />
          <h2 className="text-white font-['Montserrat',Helvetica] text-lg font-medium">
            {t('editCharacterImage')}
          </h2>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto dark-scrollbar pt-4 px-6 pb-4 space-y-4">
        {/* Input Section - Show when idle or generating */}
        {(imageGenStatus === 'idle' || imageGenStatus === 'generating') && (
          <>
            {/* Base64 Preview */}
            <div className="w-[40%] mx-auto">
              <div className="bg-[#2a2a2e] border border-white/10 rounded-xl p-2">
                {isLoadingBase64 ? (
                  <div className="flex items-center justify-center h-32 text-gray-400">
                    <div className="text-center">
                      <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" />
                      <p className="text-sm">{t('loading')}</p>
                    </div>
                  </div>
                ) : base64Image ? (
                  <div 
                    className="relative w-full aspect-[9/16] cursor-pointer group"
                    onClick={() => setShowImagePreview(true)}
                  >
                    <img
                      src={base64Image}
                      alt="Reference image"
                      className="w-full h-full object-cover rounded-lg group-hover:opacity-90 transition-opacity"
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-32 text-gray-400">
                    <div className="text-center">
                      <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">{t('noImage')}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="text-center mb-1">
              <button
                onClick={() => setShowImageSelectionPopup(true)}
                className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer"
              >
                {t('clickToChangeBaseImage')}
              </button>
            </div>

            {/* Image prompt input */}
            <div className="space-y-2 relative">
              <textarea
                ref={textareaRef}
                value={imagePrompt}
                onChange={(e) => {
                  setImagePrompt(e.target.value);
                  autoResizeTextarea();
                }}
                placeholder=""
                className="w-full py-2 px-4 bg-[#2a2a2e] border border-white/10 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-[#5856d6] transition-colors overflow-hidden resize-none"
                rows={1}
                disabled={imageGenStatus === 'generating'}
              />
              {!imagePrompt && (
                <div className="absolute top-0 left-4 text-gray-400 pointer-events-none whitespace-pre-line">
                  {t('enterDetailedPrompt')}
                </div>
              )}
            </div>
          </>
        )}

        {/* Success Section */}
        {(imageGenStatus === 'success' || imageGenStatus === 'applying') && generatedImage && (
          <div className="space-y-4">
            {/* Generated Image */}
            <div className="w-[40%] mx-auto">
              <div className="bg-[#2a2a2e] border border-white/10 rounded-xl p-2">
                <div 
                  className="relative w-full aspect-[9/16] cursor-pointer group"
                  onClick={() => setShowImagePreview(true)}
                >
                  <img
                    src={generatedImage}
                    alt="Generated image"
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
              </div>
            </div>
            {/* Gems used info */}
            <div className="text-center">
              <div className="text-sm text-gray-400">
                {t('generationCost', { price: getCurrentImageModelPrice(globalChatStateData.userChatSetting.imageModel) })}
              </div>
            </div>
          </div>
        )}


        {/* Error Section */}
        {imageGenStatus === 'error' && errorMessage && (
          <div className="text-center py-4">
            <X className="w-12 h-12 mx-auto mb-4 text-red-400" />
            <p className="text-white text-lg font-medium">{t('generationFailed')}</p>
            <p className="text-red-400 text-sm mt-2">{errorMessage}</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex gap-3 border-t border-white/10 flex-shrink-0 p-4">
        {/* Idle state - Cancel and Generate */}
        {imageGenStatus === 'idle' && (
          <>
            <Button
              variant="ghost"
              className="flex-1 text-white hover:bg-[#2a2a2e] rounded-xl"
              onClick={onClose}
            >
              {t('cancel')}
            </Button>
            <Button
              className="flex-1 bg-[#5856d6] hover:bg-[#5856d6] text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleGenerateImage}
              disabled={!imagePrompt.trim() || isGenerating || !base64Image}
            >
              {t('generateGems', { price: getCurrentImageModelPrice(globalChatStateData.userChatSetting.imageModel) })}
            </Button>
          </>
        )}

        {/* Generating state - Only Generate button with loading state */}
        {imageGenStatus === 'generating' && (
          <div className="flex justify-center w-full">
            <Button
              className="px-8 bg-[#5856d6] text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={true}
            >
              <span className="flex items-center gap-2">
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('generating')}
              </span>
            </Button>
          </div>
        )}

        {/* Success state - Go Back, Keep Editing, and Done */}
        {imageGenStatus === 'success' && (
          <>
            <Button
              variant="ghost"
              className="flex-1 text-white hover:bg-[#2a2a2e] rounded-xl"
              onClick={handleRegenerate}
            >
              {t('goBack')}
            </Button>
            <Button
              variant="ghost"
              className="flex-1 text-white hover:bg-[#2a2a2e] rounded-xl"
              onClick={handleKeepEditing}
            >
              {t('edit')}
            </Button>
            <Button
              className="flex-1 bg-[#5856d6] hover:bg-[#5856d6] text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleApplyImage}
              disabled={isApplying}
            >
              <Check className="w-4 h-4 mr-2" />
              {t('done')}
            </Button>
          </>
        )}

        {/* Applying state - Only loading */}
        {imageGenStatus === 'applying' && (
          <div className="flex justify-center w-full">
            <Button
              className="px-8 bg-[#5856d6] text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={true}
            >
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('applying')}
              </span>
            </Button>
          </div>
        )}

        {/* Error state - Try Again only */}
        {imageGenStatus === 'error' && (
          <div className="flex justify-center w-full">
            <Button
              className="px-8 bg-[#5856d6] hover:bg-[#5856d6] text-white rounded-xl"
              onClick={handleRegenerate}
            >
              {t('tryAgain')}
            </Button>
          </div>
        )}
      </div>

      {/* Image Selection Popup */}
      <ImageSelectionPopup
        isOpen={showImageSelectionPopup}
        onClose={() => setShowImageSelectionPopup(false)}
        images={galleryImages}
        selectedImageUri={selectedImage?.imageUrl}
        onImageSelect={handleImageSelect}
        onApply={handleApplySelectedImage}
      />

      {/* Full Screen Image Preview */}
      <Base64ImagePreview
        isOpen={showImagePreview}
        onClose={() => setShowImagePreview(false)}
        imageSrc={generatedImage || base64Image || ''}
        alt={generatedImage ? "Generated image preview" : "Reference image preview"}
      />

      {/* Insufficient Credit Popup */}
      <InsufficientCreditPopup
        isOpen={showInsufficientCreditPopup}
        onClose={() => setShowInsufficientCreditPopup(false)}
      />
    </PopupTemplate>
  );
}